import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { PageHeader } from "@/components/widgets";
import { levelColor } from "@/lib/helpers";
import { Skeleton } from "@/components/ui/skeleton";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Route, Clock, MapPin, AlertTriangle, CloudRain, Droplets, Eye, Thermometer, Navigation,
} from "lucide-react";

export default function Drive() {
  const [traffic, setTraffic] = useState(null);
  const [weather, setWeather] = useState(null);

  useEffect(() => {
    api.get("/traffic").then((r) => setTraffic(r.data)).catch(() => {});
    api.get("/weather").then((r) => setWeather(r.data)).catch(() => {});
  }, []);

  return (
    <div className="space-y-6" data-testid="drive-page">
      <PageHeader title="Drive Smart" subtitle="Traffic & climate intelligence for Lagos." icon={Route} />

      <Tabs defaultValue="traffic">
        <TabsList className="grid w-full grid-cols-2 rounded-full h-12 p-1 bg-zinc-100">
          <TabsTrigger value="traffic" data-testid="tab-traffic" className="rounded-full data-[state=active]:bg-white">Traffic</TabsTrigger>
          <TabsTrigger value="climate" data-testid="tab-climate" className="rounded-full data-[state=active]:bg-white">Climate</TabsTrigger>
        </TabsList>

        <TabsContent value="traffic" className="mt-5 space-y-4">
          {!traffic ? <Skeleton className="h-40 rounded-3xl" /> : (
            <>
              <div className={`rounded-3xl border p-6 ${levelColor[traffic.level]}`}>
                <div className="flex items-center justify-between">
                  <span className="text-xs font-bold uppercase tracking-wider">Current status</span>
                  <Route size={20} />
                </div>
                <div className="mt-2 font-display text-2xl font-bold">{traffic.status}</div>
                <p className="mt-1 text-sm">{traffic.message}</p>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="rounded-3xl border border-zinc-200 bg-white p-5">
                  <Clock className="text-emerald-600" size={20} />
                  <div className="mt-3 font-display text-2xl font-bold">{traffic.eta_min} min</div>
                  <div className="text-xs text-zinc-400">Estimated travel time</div>
                </div>
                <div className="rounded-3xl border border-zinc-200 bg-white p-5">
                  <MapPin className="text-emerald-600" size={20} />
                  <div className="mt-3 text-sm font-semibold text-zinc-800">{traffic.route}</div>
                  <div className="text-xs text-zinc-400">Current route</div>
                </div>
              </div>

              <div className="rounded-3xl border border-zinc-200 bg-white p-5">
                <div className="flex items-center gap-2 text-zinc-700"><Navigation size={18} className="text-emerald-600" /><span className="text-sm font-semibold">Alternative route</span></div>
                <p className="mt-2 text-sm text-zinc-600">{traffic.alt_route}</p>
                <div className="mt-3 rounded-xl bg-zinc-50 p-3 text-xs text-zinc-400">🗺️ Live map coming soon — Google Maps / Mapbox integration ready.</div>
              </div>

              <div>
                <h3 className="mb-2 font-display font-semibold">Road alerts</h3>
                <div className="space-y-2">
                  {traffic.incidents.map((inc, i) => (
                    <div key={i} className="flex items-center gap-3 rounded-2xl border border-zinc-200 bg-white p-4">
                      <div className={`grid h-9 w-9 place-items-center rounded-xl ${inc.severity === "medium" ? "bg-red-50 text-red-600" : "bg-amber-50 text-amber-600"}`}>
                        <AlertTriangle size={16} />
                      </div>
                      <div>
                        <div className="text-sm font-medium text-zinc-800">{inc.type}</div>
                        <div className="text-xs text-zinc-400">{inc.location}</div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </>
          )}
        </TabsContent>

        <TabsContent value="climate" className="mt-5 space-y-4">
          {!weather ? <Skeleton className="h-40 rounded-3xl" /> : (
            <>
              <div className={`rounded-3xl border p-6 ${levelColor[weather.level]}`}>
                <div className="flex items-center justify-between">
                  <span className="text-xs font-bold uppercase tracking-wider">Right now</span>
                  <CloudRain size={20} />
                </div>
                <div className="mt-2 font-display text-2xl font-bold">{weather.condition}</div>
                <p className="mt-1 text-sm">{weather.message}</p>
              </div>

              <div className="grid grid-cols-2 gap-4">
                {[
                  { icon: Thermometer, v: weather.temp_c != null ? `${weather.temp_c}°C` : "—", l: "Temperature" },
                  { icon: Droplets, v: `${weather.rain_chance}%`, l: "Rain chance" },
                  { icon: CloudRain, v: weather.flood_risk, l: "Flood risk" },
                  { icon: Eye, v: weather.visibility, l: "Visibility" },
                ].map((x) => (
                  <div key={x.l} className="rounded-3xl border border-zinc-200 bg-white p-5">
                    <x.icon className="text-emerald-600" size={20} />
                    <div className="mt-3 font-display text-xl font-bold">{x.v}</div>
                    <div className="text-xs text-zinc-400">{x.l}</div>
                  </div>
                ))}
              </div>

              <div className="rounded-3xl border border-emerald-200 bg-emerald-50 p-5">
                <div className="text-sm font-semibold text-emerald-800">Driving recommendation</div>
                <p className="mt-1 text-sm text-emerald-700">{weather.recommendation}</p>
              </div>
            </>
          )}
        </TabsContent>
      </Tabs>
    </div>
  );
}
